import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from 'app/auth/models/User';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { UserLocalStorageService } from 'app/permission/services/user.local.storage.service';
import { MultiAgency } from 'app/shared/models/multi-agency';
import { MultiAgencyService } from 'app/shared/multi-agency/multi-agency.service';
import { Observable } from 'rxjs/Observable';
import { MenuStore } from '../../menus/service/menu.store';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { LogService } from '../../shared/log/log.service';
import { OnyxEvent } from '../models/event';
import { RatePlanCode } from '../models/rate-plan-code';
import { EventSearchParametersStore } from '../service/event-search-parameters.store';
import { EventStore } from '../service/event.store';
import { EventCollisionModalComponent } from 'app/shared/event-collision-modal/event-collision-modal.component';
import { EventCollisionModalConfig } from 'app/shared/event-collision-modal/event-collision-modal-config';
import { EventCollisionMatch } from '../models/event-collision-match';
import { MatchingPropertyStore } from '../service/matching-property.store';
import { MergeMatchInfo } from '../models/merge-match-info';
import { MergeEventRequest } from '../models/merge-event-request';
import { PartyType } from '../models/party-type';
import { EventCollisionModalResponseEnum } from 'app/shared/event-collision-modal/event-collision-modal-response.enum';
import { Subject } from 'rxjs/Subject';
import { GuidGeneratorStore } from '../service/guid-generator.store';
import { EventCollisionModalMultipromptComponent }
         from 'app/shared/event-collision-modal-multiprompt/event-collision-modal-multiprompt.component';
import { EventCollisionModalResponse } from 'app/shared/event-collision-modal/event-collision-modal-response';
import { TranslateService } from '@ngx-translate/core';
import { EventUtils } from '../utils/event-utils';
import { EventInfoForEventCollision } from '../models/event-info-for-event-collision';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MatchingAgencyService } from '../../shared/matching-agency/matching-agency.service';

@Component({
  selector: 'pioneer-add-event',
  templateUrl: './add-event.component.html',
})
export class AddEventComponent implements OnInit {
  public eventForm: FormGroup;

  public onyxEvent: OnyxEvent;

  public isReadOnly: boolean;

  private user: User;

  public clientContactEditable = true;

  public propertyEmail: string;

  public agencyEmail: string;

  public formModelToMerge: any;

  matchingEndPointConfig: any;

  @ViewChild(EventCollisionModalComponent)
  eventCollisionModal: EventCollisionModalComponent;
  public eventCollisionModalConfig = new EventCollisionModalConfig();

  @ViewChild(EventCollisionModalMultipromptComponent)
  eventCollisionModalMultiprompt: EventCollisionModalMultipromptComponent;

  focusEventNameSubject = new Subject();

  temporaryGuid: string;
  eventMatchInfo: Array<MergeMatchInfo> = [];
  eventCollisionMatches = new Map();

  get isDirty() {
    if (!this.eventForm) {
      return false;
    }
    if (this.eventForm.disabled) {
      return false;
    }
    if (this.eventForm.dirty) {
      return true;
    }
    if (EventUtils.serializeEvent(this.onyxEvent) !== EventUtils.serializeEvent(new OnyxEvent())) {
      return true;
    }
    return this.isSelectedAgency;
  }

  private isSelectedAgency = false;

  constructor(
    public router: Router,
    private formBuilder: FormBuilder,
    private eventStore: EventStore,
    private logService: LogService,
    private menuStore: MenuStore,
    private flashMessagesService: FlashMessagesService,
    private eventSearchParametersStore: EventSearchParametersStore,
    private multiAgencyService: MultiAgencyService,
    private authenticationStore: AuthenticationStore,
    private userLocalStorageService: UserLocalStorageService,
    private matchingPropertyStore: MatchingPropertyStore,
    private guidGeneratorStore: GuidGeneratorStore,
    private translateService: TranslateService,
    private matchingAgencyService: MatchingAgencyService
  ) {}

  ngOnInit() {
    this.isReadOnly = false;
    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
    });

    this.eventStore.resetSelectedEvent();

    this.onyxEvent = new OnyxEvent();
    this.onyxEvent.eventVendors.length = 0;
    this.eventForm = this.formBuilder.group({
      event: this.formBuilder.group({}),
    });
    this.menuStore.setPageHeader('EVENT.TITLE_ADD', false);

    this.multiAgencyService.setEventSelected(true);

    this.authenticationStore.clientContactEditable$.subscribe((value) => {
      this.clientContactEditable = value;
    });

    this.guidGeneratorStore.generateGuid('EV').subscribe((value) => {
      this.temporaryGuid = value;
    });

    this.eventMatchInfo = [];
    this.eventCollisionMatches = new Map();

  }

  onSaveBtn(model: any): Observable<any> {
    const isPropertyUser = this.authenticationStore.isPropertyUser();

    if (this.authenticationStore.isPropertyUser()) {
      // Return an error when property email is missing
      if (!model.eventProperty.contact.email) {
        let error = this.translateService.instant('MESSAGES.ERROR.MISSING_CONTACT_EMAIL');
        this.flashMessagesService.error(error);
        this.logService.error(error);
        return Observable.empty();
      }
    }

    const runMatchingAgency = Boolean(
      this.authenticationStore.isPropertyUser()
      && model.eventAgency
      && model.eventAgency.code
      && model.eventProperty.chain
      && model.eventAgency.newTravelAgency);

    // Matching Agency result may update the current model
    const finalModel$ = this.matchingAgencyService.matchingAgencyProcess(model, runMatchingAgency);

    // Once the final model is resolved, event collision process is invoked.
    const eventCollision$ = finalModel$.pipe(
      map((finalModel: any) => {
        let info: EventInfoForEventCollision = {
          event: finalModel.event,
          eventProperties: this.authenticationStore.isPropertyUser() ? [finalModel.eventProperty] : this.getPropertiesFromModel(finalModel),
          eventAgency: finalModel.eventAgency,
          eventClient: finalModel.eventClient
        };
        return info;
      }),
      switchMap((info: EventInfoForEventCollision) =>
        this.eventCollisionProcess(info)
      )
    );
    eventCollision$.subscribe();

    return eventCollision$;
  }

  save(model: any): Observable<OnyxEvent> {
    model.eventProperty = this.getEventProperty(model);
    this.eventForm.disable();

    model.event.eventAgency = model.eventAgency;
    model.event.eventClient = model.eventClient;
    model.event.eventProperty = model.eventProperty;
    model.event.eventVendors = this.getVendorsFromModel(model);

    if (!this.isMultiVendor()) {
      if (!model.event.eventProperty.commRetention) {
        model.event.eventProperty.commRetention = model.event.commRetention;
      }
      if (!model.event.eventProperty.commissionRoomPercentage) {
        model.event.eventProperty.commissionRoomPercentage = model.event.commissionRoomPercentage;
      }
    }

    const formModel = new OnyxEvent(model.event);
    const updatedRefCodes: Object = {};

    if (formModel.eventAgency) {

      if (this.authenticationStore.isAgencyUser()) {
        formModel.eventAgency.eventReferenceNumber =
          model.event.eventReferenceNumber;
      }
    }

    if (formModel.eventProperty) {
      if (this.authenticationStore.isPropertyUser()) {
        formModel.eventProperty.eventReferenceNumber =
          model.event.eventReferenceNumber;
        formModel.eventProperty.ratePlanCodes = model.event.ratePlanCodes;
      }

    }

    // It is creating a new event, so assigning start and end date to agency, property and vendors
    let _startDate: string;
    let _endDate: string;
    let _eventName: string;
    if (model.event.startDate) {
      _startDate = new Date(model.event.startDate).toISOString();

      formModel.eventAgency['startDate'] = _startDate;
      formModel.eventProperty['startDate'] = _startDate;
    }
    if (model.event.endDate) {
      _endDate = new Date(model.event.endDate).toISOString();

      formModel.eventAgency['endDate'] = _endDate;
      formModel.eventProperty['endDate'] = _endDate;
    }
    if (model.event.name) {
      _eventName = model.event.name;

      formModel.eventAgency['partyEventName'] = _eventName;
      formModel.eventProperty['partyEventName'] = _eventName;
    }
    if (formModel.eventVendors && formModel.eventVendors !== undefined) {
      for (let i = 0; i < formModel.eventVendors.length; i++) {
        if (formModel.eventVendors[i] && formModel.eventVendors[i] !== undefined) {
          if (model.event.startDate) {
            formModel.eventVendors[i]['startDate'] = _startDate;
          }
          if (model.event.endDate) {
            formModel.eventVendors[i]['endDate'] = _endDate;
          }
          if (model.event.name) {
            formModel.eventVendors[i]['partyEventName'] = _eventName;
          }
        }
      }
    }

    // Set userRef fields
    this.buildUserRefFields(model, formModel);

    // checks if the user did not click the add button
    if (model.event.ratePlanCode) {
      formModel.ratePlanCodes.push(
        new RatePlanCode({ ratePlanCode: model.event.ratePlanCode })
      );
    }

    this.logService.debug(`updatedRefCodes ${updatedRefCodes}`);
    const updatedModel = formModel.merge(updatedRefCodes) as OnyxEvent;

    if (formModel.eventClient) {
      updatedModel.eventClient.code = formModel.eventClient.code;
    }

    let obs;
    if (this.eventCollisionMatches.size > 0) {
      let eventInfo = [];
      this.eventCollisionMatches.forEach(function(value, key) {
        eventInfo.push(value);
      });
      let partyInitiatorType = null;
      let partyInitiatorCode = null;
      if (this.authenticationStore.isPropertyUser()) {
        partyInitiatorType = updatedModel.eventProperty.partyType.toString();
        partyInitiatorCode = updatedModel.eventProperty.code;
      } else {
        partyInitiatorType = updatedModel.eventAgency.partyType.toString();
        partyInitiatorCode = updatedModel.eventAgency.code;
      }
      const mergeRequest: MergeEventRequest = new MergeEventRequest({
        action: 'ADD_EVENT',
        user_id: this.user.username,
        party_initiator_type: partyInitiatorType,
        party_initiator_code: partyInitiatorCode,
        merge_info: eventInfo,
        event: updatedModel,
        ev_tmp_guid: this.temporaryGuid
      });
      obs = this.sendMergeMatchingRequest(mergeRequest);
    } else {
      const finalUpdatedModel = updatedModel.merge({'evTmpGuid': this.temporaryGuid}) as OnyxEvent;
      if (this.authenticationStore.isPropertyUser()) {
        obs = this.sendAddEventRequest(finalUpdatedModel);
      } else {
        obs = this.sendAddAgencyEventRequest(finalUpdatedModel);
      }
    }
    obs.subscribe();
    return obs;
  }

  private buildUserRefFields(model: any, formModel: OnyxEvent) {
    if (model.event.customFields) {
      if (model.event.customFields.userRef01) {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef01'] = model.event.customFields.userRef01 :
          formModel.eventProperty['userRef01'] = model.event.customFields.userRef01;
      } else {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef01'] = null : formModel.eventProperty['userRef01'] = null;
      }
      if (model.event.customFields.userRef02) {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef02'] = model.event.customFields.userRef02 :
          formModel.eventProperty['userRef02'] = model.event.customFields.userRef02;
      } else {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef02'] = null : formModel.eventProperty['userRef02'] = null;
      }
      if (model.event.customFields.userRef03) {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef03'] = model.event.customFields.userRef03 :
          formModel.eventProperty['userRef03'] = model.event.customFields.userRef03;
      } else {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef03'] = null : formModel.eventProperty['userRef03'] = null;
      }
      if (model.event.customFields.userRef04) {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef04'] = model.event.customFields.userRef04 :
          formModel.eventProperty['userRef04'] = model.event.customFields.userRef04;
      } else {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef04'] = null : formModel.eventProperty['userRef04'] = null;
      }
      if (model.event.customFields.userRef05) {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef05'] = model.event.customFields.userRef05 :
          formModel.eventProperty['userRef05'] = model.event.customFields.userRef05;
      } else {
        this.authenticationStore.isAgencyUser() ?
          formModel.eventAgency['userRef05'] = null : formModel.eventProperty['userRef05'] = null;
      }
    }
  }

  sendAddEventRequest(originalEvent: OnyxEvent): Observable<OnyxEvent> {
    let returnedEvent = this.eventStore
      .addEvent(originalEvent)
      .catch((error: string) => {
        this.eventForm.enable();
        if (error.includes('Agency Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_AGENCY_REFERENCE_NUMBER', error);
        } else if (error.includes('Property Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_PROPERTY_REFERENCE_NUMBER', error);
        } else if (error.includes('The event must have one payor-type hotel')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_NO_PAYOR_TYPE_HOTEL', error);
        } else if (error.includes('The event must have only one payor-type hotel')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_ONLY_ONE_PAYOR_TYPE_HOTEL', error);
        } else if (error.includes('Legal Entity data is missing')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_LEGAL_ENTITY_DATA_IS_MISSING', error);
        } else if (error.includes('Invalid Tax ID')) {
          this.flashMessagesService.error('MESSAGES.ERROR.INVALID_VAT_ID', error);
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT', error);
        }
        return Observable.throw(error);
      })
      .map((res) => {
        return this.refreshAfterAdding(res);
      });
    return returnedEvent;
  }

  sendAddAgencyEventRequest(originalEvent: OnyxEvent): Observable<OnyxEvent> {
    let returnedEvent = this.eventStore
      .addAgencyEvent(originalEvent)
      .catch((error: string) => {
        this.eventForm.enable();
        if (error.includes('Agency Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_AGENCY_REFERENCE_NUMBER', error);
        } else if (error.includes('Property Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_PROPERTY_REFERENCE_NUMBER', error);
        } else if (error.includes('The event must have one payor-type hotel')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_NO_PAYOR_TYPE_HOTEL', error);
        } else if (error.includes('The event must have only one payor-type hotel')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_ONLY_ONE_PAYOR_TYPE_HOTEL', error);
        } else if (error.includes('Legal Entity data is missing')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_LEGAL_ENTITY_DATA_IS_MISSING', error);
        } else if (error.includes('Invalid Tax ID')) {
          this.flashMessagesService.error('MESSAGES.ERROR.INVALID_VAT_ID', error);
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT', error);
        }
        return Observable.throw(error);
      })
      .map((res) => {
        return this.refreshAfterAdding(res);
      });
    return returnedEvent;
  }

  private refreshAfterAdding(res: OnyxEvent) {
    let changeIata = false;
    if (
      this.user &&
      this.user.allArcIatas &&
      this.user.customerInfo &&
      this.user.customerInfo.length >= 2
    ) {
      const key =
        this.user.username.toUpperCase() +
        AuthenticationStore.CURRENT_AGENCY_STR;
      const currentAgency = JSON.parse(
        this.userLocalStorageService.getItem(key)
      );
      if (currentAgency.agencyId !== res.eventAgency.code) {
        changeIata = true;
      }
    }
    if (changeIata) {
      const agency: MultiAgency = new MultiAgency({
        agencyId: res.eventAgency.code,
        agencyName: res.eventAgency.name,
      });
      this.authenticationStore
        .updateIataFromToken(agency.agencyId)
        .subscribe(() => {
          this.multiAgencyService.setAgencySelectedUI(true);
          this.multiAgencyService.setDefaultAgency(agency);
          this.goToContractSummary(res.eventId, res.eventProperty.eventPartyId);
          return res;
        });
    } else {
      this.goToContractSummary(res.eventId, res.eventProperty.eventPartyId);
      return res;
    }
  }

  returnToSearchList(): void {
    this.eventForm.disable();
    this.eventSearchParametersStore.updateUrl();
  }

  private goToContractSummary(eventId: number, eventPartyId: number) {
    if (this.isMultiVendor() || !eventPartyId) {
      this.router.navigate(['/event', eventId, 'summary']);
    } else {
      this.router.navigate(['/event', eventId, eventPartyId, 'summary']);
    }
  }

  setupPropertyEmail(email: string): void {
    this.propertyEmail = email;
  }

  setupAgencyEmail(email: string): void {
    this.agencyEmail = email;
  }

  public confirmBeforeSaveEventHandler(response: EventCollisionModalResponse) {
    if (response.selectedButton === EventCollisionModalResponseEnum.MergeEvents) {
      this.eventMatchInfo.forEach((item: any) => {
        if (response.selectedMatches.indexOf(item.event_match_id) > -1) {
          let key = item['create_party'] === 'PROPERTY' ? item['matched_agency_id'] : item['matched_vendor_code'];
          this.eventCollisionMatches.set(key, item);
        }
      });
    }
    this.eventMatchInfo = [];
    this.save(this.eventForm.value);
  }

  cancelMatchingWizard(response: EventCollisionModalResponse) {
    let matchesToCancel = [];
    this.eventMatchInfo.forEach((item: any) => {
      if (response.selectedMatches.indexOf(item.event_match_id) === -1) {
        matchesToCancel.push(item.event_match_id);
      }
    });
    const obs = this.matchingPropertyStore
      .cancelMatching(matchesToCancel)
      .take(1)
      .catch((error) => {
        return Observable.throw(error);
      }).finally(() => {
        this.eventMatchInfo = [];
        this.eventForm.enable();
        this.focusEventNameSubject.next();
      });
    obs.subscribe();
    return obs;
  }

  eventCollisionProcess(info: EventInfoForEventCollision) {
    let obs = this.invokeEventCollisionMatching(info);
    obs.subscribe((match) => {
      if (match) {
        if (this.anyMatchFound(match)) {
          let matchesForModalPanel = [];
          let propertiesFromModalPanel = [];
          match.forEach((item: any) => {
            if (item.result === 'fuzzy') {
              this.eventMatchInfo.push(new MergeMatchInfo({
                create_party: item.create_party,
                id: item.id,
                merge_event: true,
                source: 'Manual input',
                event_match_id: item.event_match_id,
                matched_event_id: item.matches[0].event_id,
                matched_agency_id: item.matches[0].agency_id,
                matched_vendor_code: item.matches[0].vendor_code,
                edited_event_id: item['edited_event_id'],
                edited_agency_id: item['edited_agency_id'],
                edited_vendor_code: item['edited_vendor_code'],
                action: item['action'],
                user_id: item['user_id'],
                ev_tmp_guid: item['ev_tmp_guid']
              }));
              matchesForModalPanel.push(item);
              propertiesFromModalPanel.push(`${item.property_name} ${item.property_address}`);
            }
          });
          const eventMatch = new EventCollisionMatch({
            createdBy: this.authenticationStore.isPropertyUser() ? info.eventProperties[0].name :
                                                                   info.eventAgency.name,
            clientCompany: info.eventClient.name ? info.eventClient.name : '',
            eventName: info.event.name,
            groupCode: info.event.eventReferenceNumber,
            startDate: info.event.startDate,
            endDate: info.event.endDate,
            property: matchesForModalPanel.length > 1 ? '' : propertiesFromModalPanel[0],
            eventMatchId: matchesForModalPanel.length > 1 ? null : matchesForModalPanel[0].event_match_id,
          });
          if (matchesForModalPanel.length > 1) {
            this.eventCollisionModalMultiprompt.open(eventMatch, matchesForModalPanel);
          } else {
            this.eventCollisionModal.open(eventMatch, matchesForModalPanel[0].matches[0]);
          }
        } else {
          return this.save(this.eventForm.value);
        }
      }
      return match;
    });
    return obs;
  }

  invokeEventCollisionMatching(eventInfo: EventInfoForEventCollision): Observable<any> {
    let returnedEvent = this.matchingPropertyStore
      .getMatchingCollisionProperties(eventInfo, null, this.user.username, this.temporaryGuid)
      .catch((error) => {
        this.flashMessagesService.error('MESSAGES.ERROR.MATCHING_EVENT', error);
        return Observable.throw(error);
      })
      .map((res) => {
        return res;
      });
    return returnedEvent;
  }

  sendMergeMatchingRequest(eventInfo: any): Observable<any> {
    let returnedEvent = this.matchingPropertyStore
      .updateEventCollisionMatch(eventInfo)
      .catch((error: string) => {
        this.eventForm.enable();
        if (error.includes('Agency Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_AGENCY_REFERENCE_NUMBER', error);
        } else if (error.includes('Property Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_PROPERTY_REFERENCE_NUMBER', error);
        } else if (error.includes('The event must have one payor-type hotel')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_NO_PAYOR_TYPE_HOTEL', error);
        } else if (error.includes('The event must have only one payor-type hotel')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_ONLY_ONE_PAYOR_TYPE_HOTEL', error);
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.MERGE_MATCHING_EVENT', error);
        }
        return Observable.throw(error);
      })
      .map((res) => {
        return this.refreshAfterAdding(res);
      });
    return returnedEvent;
  }

  getVendorsFromModel(model: any) {
    let vendors = [];
    this.onyxEvent.eventVendors.forEach(function (vendor) {
      const vendorCode = vendor.code ? vendor.code : vendor.chain + '|' + vendor.brand + '|' + vendor.propertyId;
      let modelVendor = model['eventVendor-' + vendorCode];
      modelVendor.taxResponseList = vendor.taxResponseList;
      vendors.push(modelVendor);
    });

    return vendors;
  }

  getPropertiesFromModel(model: any) {
    let vendors = [];

    this.onyxEvent.eventVendors.forEach(function (vendor) {
      if (vendor.partyType === PartyType.PROPERTY && vendor.payorType === 'PROPERTY') {
        const vendorCode = vendor.code ? vendor.code : vendor.chain + '|' + vendor.brand + '|' + vendor.propertyId;
        vendors.push(model['eventVendor-' + vendorCode]);
      }
    });

    return vendors;
  }

  getEventProperty(model: any) {
    let property = {};
    if (this.isMultiVendor()) {
      this.onyxEvent.eventVendors.forEach(function (vendor) {
        if (vendor.partyType  === PartyType.PROPERTY && vendor.payorType === 'PROPERTY') {
          property = vendor;
        }
      });
    } else {
      return model.eventProperty;
    }
    return property;
  }

  isMultiVendor() {
    return this.authenticationStore.hasPermission('MultiVendor');
  }

  anyMatchFound(match): boolean {
    let result = false;
    match.forEach((item: any) => {
      if (item.result === 'fuzzy') {
        result = true;
      }
    });
    return result;
  }

  selectedAgency() {
    this.isSelectedAgency = true;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/add-event/add-event.component.ts